import { Loader } from '@openx/components-core/lib/Loader/Loader';
import type { ListDealBase } from '@openx/types/deal';
import type { UsePackageUsedByParam } from '@openx/types/package/PackageHookTypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import type { PackageRouteParams } from '../../../../types';
import { usePackageContext } from '../../../../utils/context/PackageContext';
import { UsedByEntry, type UsedByEntryItem } from './UsedByEntry';

const FIELDS = 'name,status,start_date,end_date';
const LIMIT = 10;

export const UsedByDeals = () => {
  const [dealsList, setDealsList] = useState<ListDealBase[]>([]);

  const [dealsPage, setDealsPage] = useState(0);

  const { t } = useTranslation();
  const { packageId = '' } = useParams<PackageRouteParams>();

  const { useDealsFetch, usePackageUsedBy } = usePackageContext();

  const { dealsLoading, deals, dealsFetching } = useDealsFetch(packageId, {
    fields: `${FIELDS},deal_id`,
    limit: LIMIT,
    offset: LIMIT * dealsPage,
    sort: 'name',
  });

  const packageUsedByParam: UsePackageUsedByParam = useMemo(() => {
    return {
      filters: {
        objects: ['deal'],
        package_uid: [packageId],
      },
      networkOnly: true,
    };
  }, [packageId]);

  const { data: packageUsedByData, isLoading: packageUsedByLoading } = usePackageUsedBy(packageUsedByParam);

  const totalDealsReferenced = useMemo(() => {
    return (packageUsedByData ?? []).reduce((accumulator, currentValue) => {
      return accumulator + (currentValue.deals_referenced ?? 0);
    }, 0);
  }, [packageUsedByData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: it's needed
  useEffect(() => {
    const dealsList: ListDealBase[] = deals.objects ?? [];
    setDealsList(prevState => {
      if (dealsPage > 0) {
        return [...prevState, ...dealsList];
      }
      return [...dealsList];
    });
  }, [deals.objects]);

  const showMoreDeals = useCallback(() => setDealsPage(prev => prev + 1), []);
  const getPathToDealDetails = useCallback(id => `/deals/${id}/details`, []);

  if (dealsLoading || packageUsedByLoading) {
    return <Loader />;
  }

  return (
    <UsedByEntry
      title={t('Deals')}
      showMore={showMoreDeals}
      idRowTitle={t('Deal ID:')}
      entries={dealsList as unknown as UsedByEntryItem[]}
      hasMore={deals.has_more}
      getPathToDetails={getPathToDealDetails}
      isFetchingMore={dealsFetching}
      entriesNumber={dealsList.length}
      hiddenEntriesNumber={totalDealsReferenced - dealsList.length}
      dataTest="deals-area"
    />
  );
};
